import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { formatNumber } from 'libphonenumber-js';

import { DivisionCard, OrganizerCard, EventHealthAndSafetyCard } from '../../cards';
import { countWords, truncateString } from '../../../utils';
import { isGroupPlayEvent, isEventType, EventTypes } from '../../../utils/events';
import Location from '../Location';
import Linkify from '../../text/Linkify';
import Attachments from '../Attachments';
import { setOverviewTabState } from '../../../actions/event-actions';
import { setPlayerId } from '../../../actions/profile-actions';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import { useStatsig } from '../../hooks/useStatsig';
import { getNewClubPath } from '../../../utils/clubs';
import StatsigLink from '../../Links/StatsigLink';
import config from '../../../../config';

import styles from '../../../pages/EventProfiles/event.scss';

const OverViewTabContent = (props) => {
    const { data: event, overviewTabState = {} } = props.events.currentEvent;
    const { profile, auth, clubVerificationFee, isCurrentMemberAdmin, showEventPhoneAndEmail = true } = props;
    const { data: organizerProfileData } = profile;

    useEffect(() => {
        props.getPaidHitterProfile(event.eventDirectors[0].playerId);
    }, []);

    const isPaidHitEvent = isEventType(event, EventTypes.PAID_HIT);
    const isPaidHitterRegistered = event?.registeredMemberIds?.includes(organizerProfileData?.memberId);

    let showPlayers = true;
    if (!isCurrentMemberAdmin && (!event?.playerVisibility)) {
        showPlayers = false;
    }

    const { featureGates } = useStatsig();
    const clubTierTypeId = event?.club?.tierTypeId;
    const newClubPath = getNewClubPath(clubTierTypeId);

    return (
        <div className="row">
            <div className="col-md-8">
                {event && event.details && (
                    <>
                        <h6 className="text-uppercase">
                            Description
                        </h6>
                        <Linkify>
                            <div>
                                {countWords(event.details) < 200 && (<p className="text-line-break">{event.details}{/* <Markdown markup={event.details} /> */}</p>)}
                                {countWords(event.details) >= 200 && (
                                    <p className="text-line-break">
                                        {overviewTabState.descriptionExpanded ? event.details : truncateString(event.details, 200)}
                                        <span
                                            className={`${styles.readMore}`}
                                            onClick={() => {
                                                overviewTabState.descriptionExpanded = !overviewTabState.descriptionExpanded;
                                                props.setOverviewTabState(overviewTabState);
                                            }}
                                            data-testid="event-profile.overview.read-more-btn"
                                        >
                                            {overviewTabState.descriptionExpanded ? 'Read less' : 'Read more'}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </Linkify>
                    </>
                )}
                {!isGroupPlayEvent(event) && (
                    <>
                        <h6 className="text-uppercase">
                            Divisions
                        </h6>
                        <div className="row divisions-big">
                            {event && event.eventDivisions.map((division, i) => {
                                const showItem = overviewTabState.divisionsExpanded || i < 4;
                                return showItem ? (
                                    <div key={i} className="col-md-6">
                                        <DivisionCard
                                            user={auth.user}
                                            eventUtrType={event.utrType.value}
                                            division={division}
                                            className="cursor-unset"
                                            showFooter={true}
                                            workflowVersion={event.workflowVersion}
                                            showDetails={true}
                                            clubVerificationFee={clubVerificationFee}
                                            showPlayers={showPlayers}
                                            allowOffPlatformPayments={event.allowOffPlatformPayments}
                                            isPaidHitEvent={isPaidHitEvent}
                                            isPaidHitterRegistered={isPaidHitterRegistered}
                                            data-testid={`event-profile.overview.division-${division.id}`}
                                        />
                                    </div>
                                ) : null;
                            })}
                        </div>
                        {(event && event.eventDivisions.length > 4) && (
                            <div className="mt32">
                                <a
                                    href="#"
                                    className="underline"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        overviewTabState.divisionsExpanded = !overviewTabState.divisionsExpanded;
                                        props.setOverviewTabState(overviewTabState);
                                    }}
                                    data-testid="event-profile.overview.expand-divisions-link"
                                >
                                    {overviewTabState.divisionsExpanded ? 'Hide all divisions' : 'Show all divisions'}
                                </a>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="col-md-4">
                {event?.eventType?.id === EventTypes.PAID_HIT ? (
                    <>
                        <h6 className="text-uppercase">About</h6>
                        <div className="club-details">
                            <OrganizerCard
                                key={`headOrganizer}`}
                                organizer={organizerProfileData}
                                user={auth.user}
                                sendMessage={props.sendMessage}
                                className={'p0'}
                                paidHit={true}
                                data-testid="event-profile.overview.organizer-card"
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <StatsigLink
                            gate={featureGates.CLUB_LINKS_REDIRECT}
                            enabledTo={event ? `${config.nextAppHost}/${newClubPath}/${event.club?.id}` : ''}
                            disabledTo={event ? `/clubs/${event.club?.id}` : ''}
                            data-testid="event-profile.overview.club-link"
                        >
                            <h6 className="text-uppercase">{event && event.club.name}</h6>
                        </StatsigLink>
                        {event && (
                            <div className="club-details">
                                {event && event.eventDirectors.map((director, i) => (
                                    <OrganizerCard
                                        key={`organizer-${i}`}
                                        organizer={director}
                                        user={auth.user}
                                        sendMessage={props.sendMessage}
                                        className={'p0'}
                                        eventType={event.eventType}
                                        providerEmail={event.email}
                                        data-testid={`event-profile.overview.organizer-card-${i}`}
                                    />
                                ))}
                                {showEventPhoneAndEmail && event && (event.phone || event.email) && (
                                    <>
                                        <hr className="div" />
                                        {event && (
                                            <div className="row">
                                                {event.phone && (
                                                    <div className="col-12 mb16">
                                                        <a href={`tel:${event.phone}`} data-testid="event-profile.overview.phone-link"><i className="uts uts-contact" /> {formatNumber({
                                                            country: event.phoneCountryCode,
                                                            phone: event.phone,
                                                        }, 'National')}
                                                        </a>
                                                    </div>
                                                )}
                                                {event.email && (
                                                    <div className="col-12 mb16">
                                                        <a href={`mailto:${event.email}`} data-testid="event-profile.overview.email-link"><i className="uts uts-email" /> {event.email}</a>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        {((event.healthAndSafetyProtocols && event.healthAndSafetyProtocols.length > 0) || (event.additionalSafetyInformation !== null)) && (
                            <>
                                <h6 className="text-uppercase">Health & Safety</h6>
                                <EventHealthAndSafetyCard
                                    event={event}
                                />
                            </>
                        )}
                        {event && (
                            <Attachments />
                        )}
                        <h6 className="text-uppercase">Location</h6>
                        {event && (
                            <Location
                                event={event}
                                surface={PowerFunnelSurfaces.EVENT_GP_FIND_ACCOMMODATIONS}
                                data-testid="event.overview.location"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

function mapDispatchToProps(dispatch) {
    return {
        setOverviewTabState: data => dispatch(setOverviewTabState(data)),
        getPaidHitterProfile: (playerId) => dispatch(setPlayerId(playerId, false)),
    };
}

function mapStateToProps({ auth, events, profile }) {
    return { auth, events, profile };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OverViewTabContent));
